import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Plus, RotateCcw, Search, MoreHorizontal } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { ProgramTableSkeleton } from "@/components/ui/program-table-skeleton";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { getFormattedDateTime } from "@/utils";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import { PAGE_SIZE } from "@/constants/common.constant";
import { useAuth } from "@/hooks/use-auth";

const filters = {
  pageSize: PAGE_SIZE,
  programCode: null,
  programName: null,
  programType: "BURN",
  sortBy: "createdDate",
  sortOrder: "desc",
};

const DELAY = 500;

const ProgramsPage: React.FC = () => {
  const { refreshToken } = useAuth();
  const [searchType, setSearchType] = useState("programName"); // 'programName' or 'programCode'
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredPrograms, setFilteredPrograms] = useState<any[]>([]);
  const [totalPrograms, setTotalPrograms] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const totalPages = Math.ceil(totalPrograms / rowsPerPage);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [programToDelete, setProgramToDelete] = useState<any | null>(null);
  const [programCodeQuery, setProgramCodeQuery] = useState("");

  function debounce<T extends (...args: any[]) => void>(
    func: T,
    delay: number
  ) {
    return function (...args: Parameters<T>) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(() => {
        func(...args);
        debounceTimeout.current = null;
      }, delay);
    };
  }

  const handleApiError = async (response: Response) => {
    if (response.status === 401 || response.status === 403) {
      const refreshSuccess = await refreshToken();
      if (refreshSuccess) {
        return true;
      }
      throw new Error("Authentication failed");
    }
    return false;
  };

  async function fetchPrograms(filters = {}) {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${
          import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL
        }/program/getProgramsFromDB`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json, text/plain, */*",
          },
          body: JSON.stringify(filters),
          credentials: "include",
        }
      );

      const shouldRetry = await handleApiError(response);
      if (shouldRetry) {
        return fetchPrograms(filters);
      }

      if (!response.ok) {
        throw new Error("Failed to fetch programs");
      }

      const data = await response.json();
      setTotalPrograms(data?.totalNumberOfPrograms || 0);

      const programs = data?.programVOs?.map((program) => ({
        id: program?.id,
        name: program?.programName,
        pointsName: program?.loyaltyCurrencyId || "-",
        city: program?.city || "-",
        country: program?.country || "-",
        programCode: program?.programCode,
        currency: program?.currencyVO?.iso3CurrencyCode,
        costPerPoint: program?.costPerPoint,
        invoiceFrequency: program?.invoiceFrequencyInDays
          ? `${program.invoiceFrequencyInDays} days`
          : "-",
        status: program?.status || "active",
        created: program?.createdDateInMillis
          ? getFormattedDateTime(program?.createdDateInMillis)
          : "",
        logo: program?.logo,
        programLogoUri: program?.programLogoUri,
        invoiceFrequencyInDays: program?.invoiceFrequencyInDays,
      }));
      setFilteredPrograms(programs || []);
    } catch (err) {
      toast.error("Error in fetching programs");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchPrograms({
      ...filters,
      pageNumber: page,
      pageSize: rowsPerPage,
    });
  }, [page, rowsPerPage]);

  const resetFilters = () => {
    setSearchQuery("");
    setSearchType("programName");
    setPage(0);
    fetchPrograms({ ...filters, pageNumber: 0 });
  };

  const debouncedSearch = debounce((query) => {
    setPage(0);
    fetchPrograms({ ...filters, pageNumber: 0, [searchType]: query });
  }, DELAY);

  const debouncedProgramCodeSearch = debounce((query) => {
    setPage(0);
    fetchPrograms({ ...filters, pageNumber: 0, programCode: query });
  }, DELAY);

  console.log("Filtered Programs:", filteredPrograms);

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Loyalty Programs</h1>
          <p className="text-gray-500 mt-1">
            Manage your loyalty programs and their attributes
          </p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" asChild>
          <Link to="/rewardpoint/programs/create">
            <Plus className="h-4 w-4 mr-2" /> Add Program
          </Link>
        </Button>
      </div>

      {/* Filter section */}
      <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex-1 flex gap-2 items-end">
          <div>
            <Select value={searchType} onValueChange={setSearchType}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="programName">Program Name</SelectItem>
                <SelectItem value="programCode">Program Code</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                id="search"
                placeholder={`Search by ${
                  searchType === "programName" ? "program name" : "program code"
                }...`}
                className="pl-9"
                value={searchQuery}
                onChange={(e) => {
                  const query = e.target.value;
                  setSearchQuery(query);
                  setPage(0);
                  debouncedSearch(query);
                }}
              />
            </div>
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-2 px-3 py-2 text-sm"
          onClick={resetFilters}
          disabled={isLoading}
        >
          <RotateCcw className="h-4 w-4" />
          Reset
        </Button>
      </div>

      <DataTable
        title="All Programs"
        data={isLoading ? [] : filteredPrograms}
        columns={[
          {
            header: "Created Date",
            accessorKey: "created",
            cell: (row) => (
              <div className="font-medium">
                {row.created || ""}
              </div>
            ),
          },
          {
            header: "Logo",
            accessorKey: "programLogoUri",
            cell: (row) => (
              <div className="flex items-center">
                <img
                  src={
                    row.programLogoUri
                      ? `${import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL}${
                          row.programLogoUri
                        }`
                      : "/placeholder.svg"
                  }
                  alt={row.name}
                  className="h-10 w-10 object-contain rounded-md"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/placeholder.svg";
                  }}
                />
              </div>
            ),
          },
          {
            header: "Code",
            accessorKey: "programCode",
          },
          {
            header: "Name",
            accessorKey: "name",
          },
          {
            header: "Currency",
            accessorKey: "currency",
          },
          {
            header: "CPP",
            accessorKey: "costPerPoint",
            cell: (row) => row.costPerPoint ?? "-",
          },
          {
            header: "Invoice Frequency",
            accessorKey: "invoiceFrequency",
            cell: (row) => (
              <div className="font-medium">{row.invoiceFrequency}</div>
            ),
          },
          {
            header: "Actions",
            id: "actions",
            cell: (row) => (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-white min-w-[8rem] p-1"
                >
                  <DropdownMenuItem asChild>
                    <Link to={`/rewardpoint/programs/${row.id}`}>
                      View Details
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to={`/rewardpoint/program-categories?programId=${row.id}`}
                    >
                      Program Categories
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setProgramToDelete(row);
                      setDeleteDialogOpen(true);
                    }}
                    className="text-red-600"
                  >
                    Delete Program
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ),
          },
        ]}
        customTableBody={isLoading ? <ProgramTableSkeleton /> : undefined}
      />

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Rows per page:</span>
          <Select
            value={rowsPerPage.toString()}
            onValueChange={(value) => {
              setRowsPerPage(Number(value));
              setPage(0);
            }}
          >
            <SelectTrigger className="w-[80px]">
              <SelectValue placeholder={rowsPerPage.toString()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <EnhancedPaginationControl
          totalPages={totalPages}
          currentPage={page}
          onPageChange={(page) => setPage(page)}
        />
        <div className="w-[160px]" /> {/* Spacer to balance layout */}
      </div>

      {/* Delete Program Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Program</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the program "
              {programToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                // TODO: Implement actual delete logic here
                setDeleteDialogOpen(false);
                setProgramToDelete(null);
                toast.success("Program deleted successfully");
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProgramsPage;
